import { Box, Button, Divider, Typography } from '@mui/material'
import * as React from 'react'
import { useNavigate } from 'react-router'

import { createProfileEditNavigation, generateReturnUrl, validateUserProfileForRegistration } from '../../utils/profileValidation'

/**
 * RegistrationReminder
 * Shows profile completeness reminder in booking confirmation dialogs.
 * - If profile is NOT valid: highlight requirement, block confirm (handled by parent), and offer Edit Profile.
 * - If profile is valid: gently remind to add emergency contact.
 */
const RegistrationReminder = ({ user }) => {
  const navigate = useNavigate()
  const profileValidation = validateUserProfileForRegistration(user)
  const isValid = profileValidation.isValid

  const handleEditProfile = () => {
    const returnUrl = generateReturnUrl(window.location)
    const profileEditNavigation = createProfileEditNavigation(returnUrl)
    navigate(profileEditNavigation)
  }

  // Determine whether emergency contact info is missing
  const hasEmergencyName = Boolean((user?.emergencyContact?.name || '').trim())
  const hasEmergencyPhone = Boolean((user?.emergencyContact?.phone || '').trim())
  const isEmergencyMissing = !hasEmergencyName || !hasEmergencyPhone

  // If profile is valid and emergency contact exists, render nothing
  if (isValid && !isEmergencyMissing) {
    return null
  }

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Typography variant="h6" color="text.secondary" gutterBottom>
        Important Information{!isValid ? ' (Required)' : ''}:
      </Typography>
      <Box sx={{ mt: 0, p: 2, bgcolor: isValid ? 'info.lighter' : 'warning.lighter', borderRadius: 1, border: '1px solid', borderColor: isValid ? 'info.light' : 'warning.light' }}>
        {!isValid ? (
          <>
            <Typography variant="body2" color="text.secondary">
              Please make sure your profile has a valid{' '}
              <Box component="span" sx={{ fontWeight: 'bold', color: 'info.dark' }}>
                phone number
              </Box>{' '}
              or{' '}
              <Box component="span" sx={{ fontWeight: 'bold', color: 'info.dark' }}>
                WeChat ID
              </Box>
              .
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'error.main', fontWeight: 600 }}>
              Contact information is required to complete registration.
            </Typography>
            <Button variant="contained" size="medium" onClick={handleEditProfile} sx={{ mt: 1 }}>
              Edit Profile
            </Button>
          </>
        ) : (
          // Profile is valid but emergency contact is missing
          <>
            <Typography variant="body2" color="text.secondary">
              Please add an emergency contact in your profile to help your instructor reach you in urgent situations.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
              Your personal information is only visible to your instructor.
            </Typography>
            <Button variant="outlined" size="medium" onClick={handleEditProfile} sx={{ mt: 1 }}>
              Edit Profile
            </Button>
          </>
        )}
      </Box>
    </>
  )
}

export default RegistrationReminder
